<template>
  <div style="padding: 30px">
    <div
      v-if="is_loading"
      class="text-center"
      style="padding: 80px 0px"
    >
      <img
        :src="require('@/assets/images/misc/loading.gif')"
        alt=""
        width="100px"
      />
    </div>

    <div v-else>
      <div class="d-flex justify-space-between align-center mb-4">
        <h2 class="text-h5">
          Etsy Configuration Options
        </h2>
        <v-btn
          color="primary"
          :loading="is_fetching_etsy_data"
          :disabled="is_loading || is_fetching_etsy_data"
          class="text-capitalize"
          @click="fetchEtsyData"
        >
          Fetch Etsy Data
        </v-btn>
      </div>

      <v-expansion-panels
        v-model="openPanels"
        multiple
        @change="onPanelsChange"
      >
        <v-expansion-panel>
          <v-expansion-panel-header>
            Categories ({{ getSectionCount('categories') }})
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-text-field
              v-model="categoriesSearch"
              label="Search categories"
              clearable
              outlined
              dense
              class="mt-2"
              @input="onCategoriesSearchInput"
              @click:clear="onCategoriesSearchInput"
            ></v-text-field>

            <v-data-table
              :headers="categoriesHeaders"
              :items="categories"
              :items-per-page="sections.categories.pagination.per_page"
              :loading="sections.categories.loading"
              hide-default-footer
              class="elevation-1 mt-2"
            >
              <template v-slot:[`item.title`]="{ item }">
                <span class="text-body-2">{{ item.title }}</span>
              </template>
            </v-data-table>

            <div class="d-flex justify-space-between align-center mt-3 table-pagination-controls">
              <v-select
                :items="perPageOptions"
                label="Rows"
                dense
                outlined
                hide-details
                style="max-width: 110px"
                :value="sections.categories.pagination.per_page"
                @change="onSectionPerPageChange('categories', $event)"
              ></v-select>
              <v-pagination
                v-if="sections.categories.pagination.last_page > 1"
                :value="sections.categories.pagination.current_page"
                :length="sections.categories.pagination.last_page"
                total-visible="7"
                @input="onSectionPageChange('categories', $event)"
              ></v-pagination>
              <div class="text-caption grey--text text--darken-1">
                Total: {{ getSectionCount('categories') }}
              </div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
          <v-expansion-panel-header>
            Shipping Profiles ({{ getSectionCount('shipping_profiles') }})
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-text-field
              v-model="sectionSearch.shipping_profiles"
              label="Search shipping profiles"
              clearable
              outlined
              dense
              class="mt-2"
              @input="onSectionSearchInput('shipping_profiles')"
              @click:clear="onSectionSearchInput('shipping_profiles')"
            ></v-text-field>

            <v-data-table
              :headers="shippingProfilesHeaders"
              :items="shippingProfiles"
              :items-per-page="sections.shipping_profiles.pagination.per_page"
              :loading="sections.shipping_profiles.loading"
              hide-default-footer
              class="elevation-1 mt-2"
            >
              <template v-slot:[`item.default_template`]="{ item }">
                <v-chip
                  x-small
                  :color="isTruthy(item.default_template) ? 'primary' : undefined"
                  :class="isTruthy(item.default_template) ? 'white--text' : 'status-chip-muted'"
                >
                  {{ isTruthy(item.default_template) ? 'Default' : 'No' }}
                </v-chip>
              </template>

              <template v-slot:[`item.is_calculated`]="{ item }">
                <v-chip
                  x-small
                  :color="isTruthy(item.is_calculated) ? 'success' : undefined"
                  :class="isTruthy(item.is_calculated) ? 'white--text' : 'status-chip-muted'"
                >
                  {{ isTruthy(item.is_calculated) ? 'Calculated' : 'Fixed' }}
                </v-chip>
              </template>

              <template v-slot:[`item.processing_days_display_label`]="{ item }">
                {{ item.processing_days_display_label || '-' }}
              </template>
            </v-data-table>

            <div class="d-flex justify-space-between align-center mt-3 table-pagination-controls">
              <v-select
                :items="perPageOptions"
                label="Rows"
                dense
                outlined
                hide-details
                style="max-width: 110px"
                :value="sections.shipping_profiles.pagination.per_page"
                @change="onSectionPerPageChange('shipping_profiles', $event)"
              ></v-select>
              <v-pagination
                v-if="sections.shipping_profiles.pagination.last_page > 1"
                :value="sections.shipping_profiles.pagination.current_page"
                :length="sections.shipping_profiles.pagination.last_page"
                total-visible="7"
                @input="onSectionPageChange('shipping_profiles', $event)"
              ></v-pagination>
              <div class="text-caption grey--text text--darken-1">
                Total: {{ getSectionCount('shipping_profiles') }}
              </div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
          <v-expansion-panel-header>
            Processing Profiles ({{ getSectionCount('processing_profiles') }})
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-text-field
              v-model="sectionSearch.processing_profiles"
              label="Search processing profiles"
              clearable
              outlined
              dense
              class="mt-2"
              @input="onSectionSearchInput('processing_profiles')"
              @click:clear="onSectionSearchInput('processing_profiles')"
            ></v-text-field>

            <v-data-table
              :headers="processingProfilesHeaders"
              :items="processingProfiles"
              :items-per-page="sections.processing_profiles.pagination.per_page"
              :loading="sections.processing_profiles.loading"
              hide-default-footer
              class="elevation-1 mt-2"
            >
              <template v-slot:[`item.is_default`]="{ item }">
                <v-chip
                  x-small
                  :color="isTruthy(item.is_default) ? 'primary' : undefined"
                  :class="isTruthy(item.is_default) ? 'white--text' : 'status-chip-muted'"
                >
                  {{ isTruthy(item.is_default) ? 'Default' : 'No' }}
                </v-chip>
              </template>

              <template v-slot:[`item.min_processing_days`]="{ item }">
                {{ item.min_processing_days }} - {{ item.max_processing_days }} days
              </template>
            </v-data-table>

            <div class="d-flex justify-space-between align-center mt-3 table-pagination-controls">
              <v-select
                :items="perPageOptions"
                label="Rows"
                dense
                outlined
                hide-details
                style="max-width: 110px"
                :value="sections.processing_profiles.pagination.per_page"
                @change="onSectionPerPageChange('processing_profiles', $event)"
              ></v-select>
              <v-pagination
                v-if="sections.processing_profiles.pagination.last_page > 1"
                :value="sections.processing_profiles.pagination.current_page"
                :length="sections.processing_profiles.pagination.last_page"
                total-visible="7"
                @input="onSectionPageChange('processing_profiles', $event)"
              ></v-pagination>
              <div class="text-caption grey--text text--darken-1">
                Total: {{ getSectionCount('processing_profiles') }}
              </div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
          <v-expansion-panel-header>
            Production Partners ({{ getSectionCount('production_partners') }})
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-text-field
              v-model="sectionSearch.production_partners"
              label="Search production partners"
              clearable
              outlined
              dense
              class="mt-2"
              @input="onSectionSearchInput('production_partners')"
              @click:clear="onSectionSearchInput('production_partners')"
            ></v-text-field>

            <v-data-table
              :headers="productionPartnersHeaders"
              :items="productionPartners"
              :items-per-page="sections.production_partners.pagination.per_page"
              :loading="sections.production_partners.loading"
              hide-default-footer
              class="elevation-1 mt-2"
            ></v-data-table>

            <div class="d-flex justify-space-between align-center mt-3 table-pagination-controls">
              <v-select
                :items="perPageOptions"
                label="Rows"
                dense
                outlined
                hide-details
                style="max-width: 110px"
                :value="sections.production_partners.pagination.per_page"
                @change="onSectionPerPageChange('production_partners', $event)"
              ></v-select>
              <v-pagination
                v-if="sections.production_partners.pagination.last_page > 1"
                :value="sections.production_partners.pagination.current_page"
                :length="sections.production_partners.pagination.last_page"
                total-visible="7"
                @input="onSectionPageChange('production_partners', $event)"
              ></v-pagination>
              <div class="text-caption grey--text text--darken-1">
                Total: {{ getSectionCount('production_partners') }}
              </div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <v-expansion-panel>
          <v-expansion-panel-header>
            Shop Sections ({{ getSectionCount('shop_sections') }})
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-text-field
              v-model="sectionSearch.shop_sections"
              label="Search shop sections"
              clearable
              outlined
              dense
              class="mt-2"
              @input="onSectionSearchInput('shop_sections')"
              @click:clear="onSectionSearchInput('shop_sections')"
            ></v-text-field>

            <v-data-table
              :headers="shopSectionsHeaders"
              :items="shopSections"
              :items-per-page="sections.shop_sections.pagination.per_page"
              :loading="sections.shop_sections.loading"
              hide-default-footer
              class="elevation-1 mt-2"
            >
              <template v-slot:[`item.is_default`]="{ item }">
                <v-chip
                  x-small
                  :color="isTruthy(item.is_default) ? 'primary' : undefined"
                  :class="isTruthy(item.is_default) ? 'white--text' : 'status-chip-muted'"
                >
                  {{ isTruthy(item.is_default) ? 'Default' : 'No' }}
                </v-chip>
              </template>
            </v-data-table>

            <div class="d-flex justify-space-between align-center mt-3 table-pagination-controls">
              <v-select
                :items="perPageOptions"
                label="Rows"
                dense
                outlined
                hide-details
                style="max-width: 110px"
                :value="sections.shop_sections.pagination.per_page"
                @change="onSectionPerPageChange('shop_sections', $event)"
              ></v-select>
              <v-pagination
                v-if="sections.shop_sections.pagination.last_page > 1"
                :value="sections.shop_sections.pagination.current_page"
                :length="sections.shop_sections.pagination.last_page"
                total-visible="7"
                @input="onSectionPageChange('shop_sections', $event)"
              ></v-pagination>
              <div class="text-caption grey--text text--darken-1">
                Total: {{ getSectionCount('shop_sections') }}
              </div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import store from '@/store'

export default {
  data() {
    return {
      shop: null,
      is_loading: false,
      is_fetching_etsy_data: false,
      openPanels: [],
      sectionKeys: ['categories', 'shipping_profiles', 'processing_profiles', 'production_partners', 'shop_sections'],
      perPageOptions: [10, 25, 50],
      counts: {
        categories: 0,
        shipping_profiles: 0,
        processing_profiles: 0,
        production_partners: 0,
        shop_sections: 0,
      },
      sections: {
        categories: {
          items: [],
          loading: false,
          loaded: false,
          pagination: {
            current_page: 1, last_page: 1, per_page: 10, total: 0,
          },
        },
        shipping_profiles: {
          items: [],
          loading: false,
          loaded: false,
          pagination: {
            current_page: 1, last_page: 1, per_page: 10, total: 0,
          },
        },
        processing_profiles: {
          items: [],
          loading: false,
          loaded: false,
          pagination: {
            current_page: 1, last_page: 1, per_page: 10, total: 0,
          },
        },
        production_partners: {
          items: [],
          loading: false,
          loaded: false,
          pagination: {
            current_page: 1, last_page: 1, per_page: 10, total: 0,
          },
        },
        shop_sections: {
          items: [],
          loading: false,
          loaded: false,
          pagination: {
            current_page: 1, last_page: 1, per_page: 10, total: 0,
          },
        },
      },
      categoriesSearch: '',
      categoriesSearchDebounce: null,
      sectionSearch: {
        shipping_profiles: '',
        processing_profiles: '',
        production_partners: '',
        shop_sections: '',
      },
      sectionSearchDebounce: {
        shipping_profiles: null,
        processing_profiles: null,
        production_partners: null,
        shop_sections: null,
      },
      categoriesHeaders: [
        { text: 'Category ID', value: 'category_id' },
        { text: 'Taxonomy ID', value: 'taxonomy_id' },
        { text: 'Title', value: 'title' },
        { text: 'Full Path Taxonomy IDs', value: 'full_path_taxonomy_ids' },
      ],
      shippingProfilesHeaders: [
        { text: 'Profile ID', value: 'id' },
        { text: 'Shipping Template ID', value: 'shipping_template_id' },
        { text: 'Title', value: 'title' },
        { text: 'Country', value: 'country' },
        { text: 'Postal Code', value: 'postal_code' },
        { text: 'Default', value: 'default_template' },
        { text: 'Type', value: 'is_calculated' },
        { text: 'Processing Label', value: 'processing_days_display_label' },
      ],
      processingProfilesHeaders: [
        { text: 'Profile ID', value: 'id' },
        { text: 'State ID', value: 'readiness_state_id' },
        { text: 'Readiness State', value: 'readiness_state' },
        { text: 'Processing Days', value: 'min_processing_days' },
        { text: 'Display Label', value: 'processing_days_display_label' },
        { text: 'Default', value: 'is_default' },
      ],
      productionPartnersHeaders: [
        { text: 'ID', value: 'id' },
        { text: 'Partner ID', value: 'production_partner_id' },
        { text: 'Partner Name', value: 'partner_name' },
        { text: 'Location', value: 'location' },
      ],
      shopSectionsHeaders: [
        { text: 'ID', value: 'id' },
        { text: 'Shop Section ID', value: 'shop_section_id' },
        { text: 'Title', value: 'title' },
        { text: 'Rank', value: 'rank' },
        { text: 'Active Listings', value: 'active_listing_count' },
        { text: 'Default', value: 'is_default' },
      ],
    }
  },
  computed: {
    categories() {
      return this.sections.categories.items
    },
    shippingProfiles() {
      return this.sections.shipping_profiles.items
    },
    processingProfiles() {
      return this.sections.processing_profiles.items
    },
    productionPartners() {
      return this.sections.production_partners.items
    },
    shopSections() {
      return this.sections.shop_sections.items
    },
  },
  mounted() {
    this.shop = store.getters.loggedInShop
    this.fetchConfigurationOptions()
  },
  methods: {
    async fetchEtsyData() {
      try {
        this.is_fetching_etsy_data = true

        const response = await axios.get(`admin/shops/${this.shop.id}/fetch-etsy-data`)
        if (!response.data.success) {
          throw new Error(response.data.message || 'Failed to fetch Etsy data')
        }

        await this.fetchConfigurationOptions()

        this.$swal.fire({
          icon: 'success',
          title: 'Completed',
          text: 'Etsy data fetched successfully and configuration options updated',
        })
      } catch (e) {
        console.error('Error fetching Etsy data:', e)
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: e?.response?.data?.message || 'Failed to fetch Etsy data',
        })
      } finally {
        this.is_fetching_etsy_data = false
      }
    },
    async fetchConfigurationOptions() {
      try {
        this.is_loading = true
        this.resetSectionData()

        const response = await axios.get(`admin/shops/${this.shop.id}/get-configuration-options`, {
          params: {
            paginated: true,
            section: 'summary',
          },
        })

        if (response.data.success && response.data.counts) {
          this.counts = { ...this.counts, ...response.data.counts }

          const openedSections = this.openPanels
            .map(panelIndex => this.sectionKeys[panelIndex])
            .filter(sectionKey => !!sectionKey)

          await Promise.all(openedSections.map(sectionKey => this.loadSection(sectionKey, { force: true })))
        }
      } catch (e) {
        console.error('Error fetching configuration options:', e)
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch configuration options',
        })
      } finally {
        this.is_loading = false
      }
    },
    resetSectionData() {
      this.sectionKeys.forEach(sectionKey => {
        this.sections[sectionKey].items = []
        this.sections[sectionKey].loading = false
        this.sections[sectionKey].loaded = false
        this.sections[sectionKey].pagination = {
          current_page: 1,
          last_page: 1,
          per_page: this.sections[sectionKey].pagination.per_page || 10,
          total: 0,
        }
      })
    },
    async onPanelsChange(panelIndexes) {
      const indices = Array.isArray(panelIndexes) ? panelIndexes : [panelIndexes]
      this.openPanels = indices.filter(index => typeof index === 'number')

      await Promise.all(
        this.openPanels.map(panelIndex => {
          const sectionKey = this.sectionKeys[panelIndex]
          if (!sectionKey || this.sections[sectionKey].loaded) {
            return Promise.resolve()
          }

          return this.loadSection(sectionKey)
        }),
      )
    },
    async loadSection(sectionKey, options = {}) {
      if (!this.shop || !this.sections[sectionKey]) {
        return
      }

      const sectionState = this.sections[sectionKey]
      if (sectionState.loading) {
        return
      }

      const page = options.page || sectionState.pagination.current_page || 1
      const perPage = options.perPage || sectionState.pagination.per_page || 10

      sectionState.loading = true

      try {
        const params = {
          paginated: true,
          section: sectionKey,
          page,
          per_page: perPage,
        }

        const sectionSearchValue = sectionKey === 'categories'
          ? this.categoriesSearch.trim()
          : (this.sectionSearch[sectionKey] || '').trim()

        if (sectionSearchValue !== '') {
          params.search = sectionSearchValue
        }

        const response = await axios.get(`admin/shops/${this.shop.id}/get-configuration-options`, { params })
        if (!response.data.success) {
          return
        }

        sectionState.items = Array.isArray(response.data.items) ? response.data.items : []
        const pagination = response.data.pagination || {}
        sectionState.pagination = {
          current_page: pagination.current_page || 1,
          last_page: pagination.last_page || 1,
          per_page: pagination.per_page || perPage,
          total: pagination.total || 0,
        }
        sectionState.loaded = true
        this.counts[sectionKey] = sectionState.pagination.total
      } catch (e) {
        console.error(`Error loading ${sectionKey}:`, e)
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: `Failed to load ${sectionKey.replace(/_/g, ' ')}`,
        })
      } finally {
        sectionState.loading = false
      }
    },
    onSectionPageChange(sectionKey, page) {
      this.loadSection(sectionKey, { page })
    },
    onSectionPerPageChange(sectionKey, perPage) {
      this.loadSection(sectionKey, { page: 1, perPage })
    },
    onCategoriesSearchInput() {
      if (this.categoriesSearchDebounce) {
        clearTimeout(this.categoriesSearchDebounce)
      }

      this.categoriesSearchDebounce = setTimeout(() => {
        this.loadSection('categories', { page: 1 })
      }, 350)
    },
    onSectionSearchInput(sectionKey) {
      if (!Object.prototype.hasOwnProperty.call(this.sectionSearchDebounce, sectionKey)) {
        return
      }

      if (this.sectionSearchDebounce[sectionKey]) {
        clearTimeout(this.sectionSearchDebounce[sectionKey])
      }

      this.sectionSearchDebounce[sectionKey] = setTimeout(() => {
        this.loadSection(sectionKey, { page: 1 })
      }, 350)
    },
    getSectionCount(sectionKey) {
      const sectionState = this.sections[sectionKey]
      if (!sectionState) {
        return 0
      }

      if (sectionState.loaded || sectionState.pagination.total > 0) {
        return sectionState.pagination.total
      }

      return this.counts[sectionKey] || 0
    },
    isTruthy(value) {
      return value === true || value === 1 || value === '1'
    },
  },
}
</script>

<style scoped>
.status-chip-muted {
  background-color: #e0e0e0 !important;
  color: #424242 !important;
  opacity: 1 !important;
}

.table-pagination-controls {
  gap: 12px;
}
</style>
